/**
 * Section Calcul du temps de l'onglet Import et paramètres — EF-CAL-001 à
 * EF-CAL-004, EF-RBK-001.
 *
 * Modifier ces réglages n'est pas un passage obligatoire avant chaque calcul :
 * l'action « Calculer » utilise les paramètres actifs.
 */

import type { JSX } from 'preact';
import { formatLocalDateTime, parseLocalDateTime, parseTimeOfDay } from '../../core/local-datetime';
import { LIMITS, validateSchedule, validateSpeed } from '../../core/parameters';
import { NumberField } from '../components/NumberField';
import { formatTimeOfDay } from '../../core/local-datetime';
import { MARKERS } from '../labels';
import { parameters, setParameters, sleepPromptNeeded } from '../state';

export function ComputeParametersSection(): JSX.Element {
  const speed = parameters.value.speed;
  const schedule = parameters.value.schedule;
  const speedValidation = validateSpeed(speed);
  const scheduleValidation = validateSchedule(schedule);

  const errorFor = (field: string): string | undefined =>
    [...speedValidation.issues, ...scheduleValidation.issues].find((issue) => issue.field === field)?.message;

  const updateSpeed = (patch: Partial<typeof speed>): void => {
    void setParameters({ ...parameters.value, speed: { ...speed, ...patch } });
  };
  const updateSchedule = (patch: Partial<typeof schedule>): void => {
    void setParameters({ ...parameters.value, schedule: { ...schedule, ...patch } });
  };

  const sleepNeeded = sleepPromptNeeded.value;

  return (
    <>
      <fieldset>
        <legend>Profil de vitesse</legend>
        <NumberField
          label="Vitesse sur le plat, pente 0 %"
          suffix="km/h"
          value={speed.referenceKmh}
          min={LIMITS.referenceKmh.min}
          max={LIMITS.referenceKmh.max}
          step={0.5}
          error={errorFor('speed.referenceKmh')}
          onChange={(value) => {
            updateSpeed({ referenceKmh: value });
          }}
        />
        <NumberField
          label="Vitesse dans une pente à +5 %"
          suffix="km/h"
          value={speed.uphill5Kmh}
          min={LIMITS.uphill5Kmh.min}
          max={LIMITS.uphill5Kmh.max}
          step={0.5}
          error={errorFor('speed.uphill5Kmh')}
          onChange={(value) => {
            updateSpeed({ uphill5Kmh: value });
          }}
        />
        <NumberField
          label="Vitesse maximale"
          suffix="km/h"
          value={speed.maxKmh}
          min={LIMITS.maxKmh.min}
          max={LIMITS.maxKmh.max}
          step={1}
          error={errorFor('speed.maxKmh')}
          onChange={(value) => {
            updateSpeed({ maxKmh: value });
          }}
        />
        <NumberField
          label="Vitesse minimale"
          suffix="km/h"
          value={speed.minKmh}
          min={LIMITS.minKmh.min}
          max={LIMITS.minKmh.max}
          step={0.5}
          error={errorFor('speed.minKmh')}
          onChange={(value) => {
            updateSpeed({ minKmh: value });
          }}
        />
        {speedValidation.warnings.map((warning) => (
          <p class="notice" key={warning.field}>
            {warning.message}
          </p>
        ))}
      </fieldset>

      <fieldset>
        <legend>Date et heure de départ</legend>
        <label>
          <span class="field-label">Date</span>
          <input
            type="date"
            value={formatLocalDateTime(schedule.startLocal).slice(0, 10)}
            onInput={(event) => {
              const date = (event.currentTarget as HTMLInputElement).value;
              const time = formatLocalDateTime(schedule.startLocal).slice(11, 16);
              const parsed = parseLocalDateTime(`${date}T${time}`);
              if (parsed !== null) updateSchedule({ startLocal: parsed });
            }}
          />
        </label>
        <label>
          <span class="field-label">Heure</span>
          <input
            type="time"
            step={60}
            value={formatLocalDateTime(schedule.startLocal).slice(11, 16)}
            onInput={(event) => {
              const time = (event.currentTarget as HTMLInputElement).value;
              const date = formatLocalDateTime(schedule.startLocal).slice(0, 10);
              const parsed = parseLocalDateTime(`${date}T${time}`);
              if (parsed !== null) updateSchedule({ startLocal: parsed });
            }}
          />
        </label>
      </fieldset>

      <fieldset>
        <legend>Pauses régulières</legend>
        <label class="checkbox">
          <input
            type="checkbox"
            checked={schedule.pauses.enabled}
            onChange={(event) => {
              updateSchedule({
                pauses: { ...schedule.pauses, enabled: (event.currentTarget as HTMLInputElement).checked },
              });
            }}
          />
          <span>Pauses régulières</span>
        </label>
        {schedule.pauses.enabled ? (
          <>
            <NumberField
              label="Toutes les"
              suffix="h"
              value={Math.round((schedule.pauses.intervalSeconds / 3600) * 2) / 2}
              min={LIMITS.pauseIntervalSeconds.min / 3600}
              max={LIMITS.pauseIntervalSeconds.max / 3600}
              step={0.5}
              error={errorFor('schedule.pauses.intervalSeconds')}
              onChange={(value) => {
                updateSchedule({ pauses: { ...schedule.pauses, intervalSeconds: Math.round(value * 3600) } });
              }}
            />
            <NumberField
              label="Durée"
              suffix="min"
              value={Math.round(schedule.pauses.durationSeconds / 60)}
              min={LIMITS.pauseDurationSeconds.min / 60}
              max={LIMITS.pauseDurationSeconds.max / 60}
              error={errorFor('schedule.pauses.durationSeconds')}
              onChange={(value) => {
                updateSchedule({ pauses: { ...schedule.pauses, durationSeconds: Math.round(value * 60) } });
              }}
            />
            {scheduleValidation.warnings.map((warning) => (
              <p class="notice" key={warning.field}>
                {warning.message}
              </p>
            ))}
          </>
        ) : null}
      </fieldset>

      <fieldset>
        <legend>Périodes de sommeil</legend>
        {sleepNeeded ? (
          <p class="notice">
            L’estimation sans sommeil dépasse 24 heures : renseignez une heure de coucher et une durée, ou
            poursuivez sans sommeil.
          </p>
        ) : null}
        <label class="checkbox">
          <input
            type="checkbox"
            checked={schedule.sleep.enabled}
            onChange={(event) => {
              const enabled = (event.currentTarget as HTMLInputElement).checked;
              updateSchedule({ sleep: { ...schedule.sleep, enabled, declinedByUser: !enabled } });
            }}
          />
          <span>Sommeil</span>
        </label>
        {schedule.sleep.enabled ? (
          <>
            <label>
              <span class="field-label">Coucher</span>
              <input
                type="time"
                value={formatTimeOfDay(schedule.sleep.bedTimeSecondsOfDay)}
                onInput={(event) => {
                  const parsed = parseTimeOfDay((event.currentTarget as HTMLInputElement).value);
                  if (parsed !== null) {
                    updateSchedule({ sleep: { ...schedule.sleep, bedTimeSecondsOfDay: parsed } });
                  }
                }}
              />
              {errorFor('schedule.sleep.bedTimeLocal') !== undefined ? (
                <span class="field-error">{errorFor('schedule.sleep.bedTimeLocal')}</span>
              ) : null}
            </label>
            <NumberField
              label="Durée"
              suffix="h"
              value={Math.round((schedule.sleep.durationSeconds / 3600) * 10) / 10}
              min={LIMITS.sleepDurationSeconds.min / 3600}
              max={LIMITS.sleepDurationSeconds.max / 3600}
              step={0.5}
              error={errorFor('schedule.sleep.durationSeconds')}
              onChange={(value) => {
                updateSchedule({ sleep: { ...schedule.sleep, durationSeconds: Math.round(value * 3600) } });
              }}
            />
          </>
        ) : (
          <p class="muted">{MARKERS.noSleep}</p>
        )}
      </fieldset>
    </>
  );
}
